import { CircularProgress, FormControl, Grid, MenuItem } from '@material-ui/core';
import { FormikProvider, useFormik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GetGeocoderFormInitialValues, GetGeocoderFormValidationSchema, IDivisionData, IGeocoderRequest } from 'src/models';
import Form from 'src/components/Form/Form';
import CustomTextField from 'src/components/CustomTextField/custom-textfield.component';
import CustomButton, { ButtonTypeEnum } from 'src/components/CustomButton/custom-button.component';
import { FindInPage, Refresh } from '@material-ui/icons';
import CountryData from 'src/assets/data/country.json';
import { Constant } from 'src/shared/constants';

export interface IGeocoderFormProps {
	getGeocoder: Function;
	setGeocoder: Function;
}

const GeocoderForm: React.FC<IGeocoderFormProps> = props => {
	const { t } = useTranslation();
	const [pincode, setPincode] = useState<string>('');
	const [disabled, setDisabled] = useState<boolean>(false);
	const [stateCodeData, setStateCodeData] = useState<IDivisionData>(CountryData[Constant.defaultCountryCode].divisions);

	const formik = useFormik<any>({
		initialValues: GetGeocoderFormInitialValues(),
		validationSchema: GetGeocoderFormValidationSchema(t),
		onSubmit: async (values: IGeocoderRequest) => {
			setDisabled(true);
			try {
				await props.getGeocoder(values);
			} catch (e) {
				setDisabled(false);
			} finally {
				setDisabled(false);
			}
		},
	});

	const handlePincodeChange = (value: string) => {
		setPincode(value.slice(0, 6));
	};

	const handleCountryCodeChange = (value: string) => {
		setStateCodeData(CountryData[value].divisions);
	};

	return (
		<>
			<FormikProvider value={formik}>
				<Form onSubmit={formik.handleSubmit}>
					<Grid container direction='column' spacing={2}>
						<Grid item sm={12}>
							<CustomTextField
								type='number'
								name={'pincode'}
								label={t('label-pincode')}
								placeholder={t('label-pincode')}
								formik={formik}
								value={pincode}
								onChange={handlePincodeChange}
							/>
						</Grid>
						<Grid item sm={12}>
							<CustomTextField
								type='text'
								name={'addressLine1'}
								label={t('label-address-line-1')}
								placeholder={t('label-address-line-1')}
								formik={formik}
							/>
						</Grid>
						<Grid item sm={12}>
							<CustomTextField
								type='text'
								name={'addressLine2'}
								label={t('label-address-line-2')}
								placeholder={t('label-address-line-2')}
								formik={formik}
							/>
						</Grid>
						<Grid item sm={12}>
							<CustomTextField
								type='text'
								name={'city'}
								label={t('label-city')}
								placeholder={t('label-city')}
								formik={formik}
							/>
						</Grid>
						<Grid item container spacing={2}>
							<Grid item sm={6}>
								<FormControl fullWidth variant='standard'>
									<CustomTextField
										name={'stateCode'}
										label={t('label-state')}
										placeholder={t('label-state')}
										formik={formik}
										fullWidth
										select
										children={Object.entries(stateCodeData).map(([stateCode, stateName], index) => (
											<MenuItem key={index} value={stateCode}>
												{`${stateName} - ${stateCode}`}
											</MenuItem>
										))}
									/>
								</FormControl>
							</Grid>
							<Grid item sm={6}>
								<FormControl fullWidth variant='standard'>
									<CustomTextField
										name={'countryCode'}
										label={t('label-country')}
										placeholder={t('label-country')}
										formik={formik}
										fullWidth
										select
										children={Object.keys(CountryData).map((countryCode, index) => (
											<MenuItem key={index} value={countryCode}>
												{`${CountryData[countryCode].name} - ${countryCode}`}
											</MenuItem>
										))}
										onChange={handleCountryCodeChange}
									/>
								</FormControl>
							</Grid>
						</Grid>
					</Grid>
					<Grid container item justifyContent='center' spacing={2} className='mt-20'>
						<Grid item>
							<CustomButton
								type={ButtonTypeEnum.Submit}
								disabled={disabled}
								startIcon={!disabled ? <FindInPage /> : <CircularProgress size={20} />}
								buttonText={t('label-get-geocoder')}
							/>
						</Grid>
						<Grid item>
							<CustomButton
								type={ButtonTypeEnum.Reset}
								onClick={() => {
									formik.resetForm();
									setPincode('');
									setStateCodeData(CountryData[Constant.defaultCountryCode].divisions);
									props.setGeocoder(null);
								}}
								startIcon={<Refresh />}
								buttonText={t('label-reset')}
								disabled={!formik.dirty || disabled}
							/>
						</Grid>
					</Grid>
				</Form>
			</FormikProvider>
		</>
	);
};

export default GeocoderForm;
